import { formatLatencyMs, prettyEnglishDate } from "@app/date";
import { formatCurrency, selectLlmGatewayConfiguration } from "@app/deploy";
import {
  currentMonthWindow,
  fetchLlmGatewayUsage,
  selectLlmGatewayUsage,
} from "@app/llm-gateway";
import { useQuery, useSelector } from "@app/react";
import { supportUrl } from "@app/routes";
import { dedent } from "@app/string-utils";
import { Box } from "@app/ui/shared/box";
import { IconInfo } from "@app/ui/shared/icons";
import { LoadingSpinner } from "@app/ui/shared/loading";
import {
  ProgressBar,
  type ProgressBarVariant,
} from "@app/ui/shared/progress-bar";
import { tokens } from "@app/ui/shared/tokens";
import { Tooltip } from "@app/ui/shared/tooltip";
import { DateTime } from "luxon";
import { useState } from "react";
import { Link } from "react-router-dom";

const UPGRADE_URL = supportUrl({
  subject: "Upgrade to paid LLM Gateway plan",
  message: dedent(
    `
    To upgrade to a paid LLM Gateway plan, submit this form and the Aptible Support team will be in touch soon.

    The LLM Gateway is $400/month plus usage fees. Learn more about pricing here: https://www.aptible.com/pricing
  `.trim(),
  ),
});

const spendVariant = (spend: number, limit: number): ProgressBarVariant => {
  if (limit === 0) return "default";
  const percent = (spend / limit) * 100;
  if (percent >= 100) return "error";
  if (percent >= 80) return "warning";
  return "default";
};

const UsageWidget = ({
  title,
  tooltip,
  isLoading,
  value,
  detail,
}: {
  title: string;
  tooltip: string;
  isLoading: boolean;
  value: string;
  detail?: string;
}) => (
  <Box size="md">
    <div className="flex flex-col gap-4">
      <h4 className="text-base font-semibold text-gray-900 flex space-x-1 items-center w-fit">
        <span>{title}</span>
        <Tooltip text={tooltip}>
          {isLoading ? (
            <LoadingSpinner variant="xs" className="inline-block ml-2" />
          ) : (
            <IconInfo
              className="inline-block opacity-50 hover:opacity-100"
              variant="sm"
            />
          )}
        </Tooltip>
      </h4>
      <div className="text-2xl">{value}</div>
      {detail ? <div className="text-sm text-gray-500">{detail}</div> : null}
    </div>
  </Box>
);

const SpendWidget = ({
  spend,
  isLoading,
}: { spend: number; isLoading: boolean }) => {
  const config = useSelector(selectLlmGatewayConfiguration);
  const spendLimit = config ? config.spendLimit : 0;
  const isTrial = config ? config.trial : false;
  const refreshesAt = DateTime.utc().plus({ months: 1 }).startOf("month");

  return (
    <Box size="md">
      <div className="flex flex-col gap-4">
        <h3 className="text-base font-semibold text-gray-900">
          {isTrial ? "Trial Credit Spend" : "Monthly Spend (MTD)"}
          <LoadingSpinner
            variant="xs"
            className="inline-block ml-2"
            show={isLoading}
          />
        </h3>
        <div className="flex flex-col">
          <ProgressBar
            value={spend}
            maxValue={spendLimit}
            variant={spendVariant(spend, spendLimit)}
            className="mb-2"
            height="lg"
          />
          <div className="flex items-start justify-between text-sm text-gray-500">
            <div className={`${tokens.type.darker} text-base`}>
              {formatCurrency(spend)} used
            </div>
            <div className="flex flex-col gap-1 items-end">
              <span className={`${tokens.type.darker} text-base`}>
                Limit: {formatCurrency(spendLimit)}
              </span>
              {isTrial ? (
                <div>
                  Ready to sign up for more credits?{" "}
                  <Link className="underline" to={UPGRADE_URL}>
                    Contact us
                  </Link>
                </div>
              ) : (
                <div>
                  Refreshes {prettyEnglishDate(refreshesAt.toISO() || "")}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Box>
  );
};

export const LlmGatewayUsageSummary = ({ orgId }: { orgId: string }) => {
  // Fixed for the life of the page, so the query key doesn't change on render.
  const [usageWindow] = useState(currentMonthWindow);
  const { isLoading } = useQuery(
    fetchLlmGatewayUsage({ orgId, ...usageWindow }),
  );
  const usage = useSelector((s) => selectLlmGatewayUsage(s, { orgId }));

  return (
    <div className="grid grid-cols-4 gap-4">
      <SpendWidget spend={usage.spend} isLoading={isLoading} />
      <UsageWidget
        title="Token Usage"
        tooltip="Tokens used across all keys this month."
        isLoading={isLoading}
        value={usage.totalTokens.toLocaleString()}
        detail={`${usage.inputTokens.toLocaleString()} input / ${usage.outputTokens.toLocaleString()} output`}
      />
      <UsageWidget
        title="Total Requests"
        tooltip="Requests made across all keys this month."
        isLoading={isLoading}
        value={usage.totalRequests.toLocaleString()}
        detail={`${usage.successfulRequests.toLocaleString()} successful / ${usage.failedRequests.toLocaleString()} failed`}
      />
      <UsageWidget
        title="Avg Latency"
        tooltip="Average response time across all keys this month."
        isLoading={isLoading}
        value={formatLatencyMs(usage.averageLatencyMs)}
      />
    </div>
  );
};
